import { useCallback, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ETiny from 'components/editors/ETiny';
import Editor from 'components/editors/Editor';
import conf from 'conf/conf';
import type { ProposalEditFormValues } from '../../Proposal/types';
import { TemplateKeysOffcanvas } from '../../share';

interface DocumentContentTabProps {
    formData: ProposalEditFormValues;
    objectData?: unknown;
    disabled?: boolean;
    onChange: (patch: Partial<ProposalEditFormValues>) => void;
}

const DocumentContentTab = ({ formData, objectData, disabled = false, onChange }: DocumentContentTabProps) => {
 
    const { t } = useTranslation('Document');
    const TinyEditor = (conf.tinyMode ? ETiny : Editor) as any;
    const [showKeys, setShowKeys] = useState(false);
    const [keysSelectMode, setKeysSelectMode] = useState(false);
    const keyInsertHandlerRef = useRef<((key: string) => void) | null>(null);

    const openKeysDirectory = useCallback(() => {
        keyInsertHandlerRef.current = null;
        setKeysSelectMode(false);
        setShowKeys(true);
    }, []);

    const openKeysForInsert = useCallback((insertKey: (key: string) => void) => {
        keyInsertHandlerRef.current = insertKey;
        setKeysSelectMode(true);
        setShowKeys(true);
    }, []);

    const handleKeysHide = useCallback(() => {
        keyInsertHandlerRef.current = null;
        setKeysSelectMode(false);
        setShowKeys(false);
    }, []);

    const handleKeySelect = useCallback((key: string) => {
        const insertKey = keyInsertHandlerRef.current;
        keyInsertHandlerRef.current = null;
        setKeysSelectMode(false);
        setShowKeys(false);
        window.setTimeout(() => {
            insertKey?.(key);
        }, 0);
    }, []);

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>{t('edit.fields.proposalIntro')}</Form.Label>
                <div className="small text-muted mb-1 d-flex justify-content-between align-items-center gap-2">
                    <span>{t('edit.fields.proposalIntroHint')}</span>
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        onClick={openKeysDirectory}
                    >
                        {t('edit.keys.openDirectory')}
                    </button>
                </div>
                <TinyEditor
                    value={formData.proposalIntro}
                    disabled={disabled}
                    autoGrow
                    onSelectKey={openKeysForInsert}
                    onChange={(value: string) => onChange({ proposalIntro: value })}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>{t('edit.fields.proposalOutro')}</Form.Label>
                <div className="small text-muted mb-1 d-flex justify-content-between align-items-center gap-2">
                    <span>{t('edit.fields.proposalOutroHint')}</span>
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        onClick={openKeysDirectory}
                    >
                        {t('edit.keys.openDirectory')}
                    </button>
                </div>
                <TinyEditor
                    value={formData.proposalOutro}
                    disabled={disabled}
                    autoGrow
                    onSelectKey={openKeysForInsert}
                    onChange={(value: string) => onChange({ proposalOutro: value })}
                />
            </Form.Group>

            <TemplateKeysOffcanvas
                show={showKeys}
                source="document"
                objectData={objectData}
                onHide={handleKeysHide}
                onKeySelect={keysSelectMode ? handleKeySelect : undefined}
            />
        </>
    );
};

export default DocumentContentTab;
